// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — News Editorial Panel
// Fixed right panel (340px), two sections: Corporativa + Reputacional
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { PM } from '../../../../../config/processMapStyles';
import { IconCamera } from './ProcessMapIcons';
import { useI18n } from '../../../../../i18n';

// ─── Placeholder data ────────────────────────────────────────

const ACTUALIDAD_CORPORATIVA = [
  {
    category: 'Normativa',
    color: '#2F5496',
    title: 'Actualización del Manual de Procedimientos aprobada',
    date: '20 Mar 2026',
  },
  {
    category: 'RRHH',
    color: '#1A94BF',
    title: 'Plan de formación Q2 publicado en intranet',
    date: '18 Mar 2026',
  },
  {
    category: 'Tecnología',
    color: '#33BCE1',
    title: 'Migración de datos completada sin incidencias',
    date: '15 Mar 2026',
  },
  {
    category: 'Gobierno',
    color: '#1F3864',
    title: 'Sesión ordinaria del Comité de Dirección celebrada',
    date: '12 Mar 2026',
  },
];

const ACTUALIDAD_REPUTACIONAL = [
  {
    category: 'Prensa',
    color: '#16507D',
    title: 'Reconocimiento en el ranking de gobernanza del sector',
    date: '19 Mar 2026',
  },
  {
    category: 'RSC',
    color: '#16A34A',
    title: 'Publicado informe de sostenibilidad 2025',
    date: '12 Mar 2026',
  },
  {
    category: 'Comunicación',
    color: '#0E243E',
    title: 'Entrevista del CEO en revista especializada',
    date: '8 Mar 2026',
  },
  {
    category: 'Reputación',
    color: '#2F5496',
    title: 'Encuesta de percepción corporativa: resultados positivos',
    date: '5 Mar 2026',
  },
];

// ═══════════════════════════════════════════════════════════════

export function NewsEditorialPanel() {
  const { t } = useI18n();

  return (
    <aside
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: PM.surface,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 16px 12px',
          borderBottom: `1px solid ${PM.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: PM.text }}>
            {t.processMap.noticias}
          </div>
          <div style={{ fontSize: 10, color: PM.textMut, marginTop: 2, letterSpacing: 0.5 }}>
            V·IANet News
          </div>
        </div>
        <button
          style={{
            fontSize: 11,
            color: PM.azulClaro,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontFamily: 'inherit',
            padding: '4px 8px',
            borderRadius: 4,
          }}
        >
          {t.processMap.verTodo}
        </button>
      </div>

      {/* Scrollable content with two sections */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Section 1 — Actualidad Corporativa */}
        <NewsSection
          title="Actualidad Corporativa"
          titleColor="#1F3864"
          items={ACTUALIDAD_CORPORATIVA}
        />

        {/* Separator */}
        <div
          style={{
            height: 1,
            background: 'linear-gradient(90deg, transparent, #D6E4F0, transparent)',
            margin: '24px 0',
          }}
        />

        {/* Section 2 — Actualidad Reputacional */}
        <NewsSection
          title="Actualidad Reputacional"
          titleColor="#1A94BF"
          items={ACTUALIDAD_REPUTACIONAL}
        />
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: `1px solid ${PM.border}`,
          fontSize: 11,
          color: PM.textSec,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {t.processMap.noticiasRecientes(
            ACTUALIDAD_CORPORATIVA.length + ACTUALIDAD_REPUTACIONAL.length,
          )}
        </span>
        <button
          style={{
            fontSize: 11,
            color: PM.azulClaro,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            fontFamily: 'inherit',
          }}
        >
          {t.processMap.verTodas}
        </button>
      </div>
    </aside>
  );
}

// ─── News Section (group of cards with title) ────────────────

function NewsSection({
  title,
  titleColor,
  items,
}: {
  title: string;
  titleColor: string;
  items: { category: string; color: string; title: string; date: string }[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: titleColor,
          letterSpacing: 0.5,
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div
          style={{
            width: 3,
            height: 16,
            borderRadius: 2,
            background: titleColor,
          }}
        />
        {title}
      </div>
      {items.map((item, idx) => (
        <SectionNewsCard key={idx} item={item} />
      ))}
    </div>
  );
}

// ─── Section News Card ───────────────────────────────────────

function SectionNewsCard({
  item,
}: {
  item: { category: string; color: string; title: string; date: string };
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #E5E7EB',
        boxShadow: hovered ? '0 4px 12px rgba(31,56,100,0.1)' : '0 1px 4px rgba(31,56,100,0.06)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        transform: hovered ? 'translateY(-1px)' : 'none',
        display: 'flex',
        gap: 10,
        padding: '12px 12px',
        alignItems: 'center',
      }}
    >
      {/* Photo placeholder */}
      <div
        style={{
          width: 48,
          height: 40,
          minWidth: 48,
          borderRadius: 8,
          backgroundColor: PM.azulSuave,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <IconCamera size={14} color={PM.textMut} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'inline-block',
            fontSize: 9,
            fontWeight: 700,
            color: '#fff',
            backgroundColor: item.color,
            padding: '1px 6px',
            borderRadius: 3,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            marginBottom: 3,
          }}
        >
          {item.category}
        </span>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: PM.text,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            marginBottom: 2,
          }}
        >
          {item.title}
        </div>
        <div style={{ fontSize: 10, color: PM.textMut }}>{item.date}</div>
      </div>
    </div>
  );
}
